import { useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { AvatarGroup, PriorityChip, StageBadge, TypeGlyph } from '@/components/common/badges'
import { AppIcon } from '@/components/common/icon-map'
import { IssueLink } from '@/components/common/issue-link'
import { IssueDataTable } from '@/components/tables/data-table'
import { cn } from '@/lib/utils'
import { daysAtStage, daysOverdue } from '@/lib/computed'
import { MISSING_METADATA } from '@/lib/stage'
import type { WorkIssue } from '@/types/github'

/** The sentinel category for the "show everything" pill. */
export const ALL_CATEGORY = 'all'

const GROUP_META: Record<string, { icon: string; varName: string }> = {
  'system/escalated': { icon: 'siren', varName: '--p0' },
  'system/stalled': { icon: 'timer-off', varName: '--p1' },
  'system/aging': { icon: 'hourglass', varName: '--p2' },
  'system/awaiting-verification-qa': { icon: 'shield-check', varName: '--st-released' },
  'system/awaiting-verification-prod': { icon: 'shield-check', varName: '--st-released' },
  'system/regression-at-prod': { icon: 'octagon-alert', varName: '--p0' },
  [MISSING_METADATA]: { icon: 'circle-dashed', varName: '--st-triage' },
}

function Pill({
  active,
  onClick,
  icon,
  varName,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  icon?: string
  varName?: string
  label: string
  count: number
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-colors',
        active
          ? 'border-border-strong bg-surface text-foreground'
          : 'border-border text-muted-foreground hover:bg-hover',
      )}
    >
      {icon && (
        <AppIcon name={icon} className="size-3.5" style={{ color: `var(${varName ?? '--st-triage'})` }} />
      )}
      <span className="font-mono">{label}</span>
      <span className="text-faint-foreground tabular-nums">{count}</span>
    </button>
  )
}

/** The category filter bar — one pill per Health group (icon + name + count),
 *  plus a leading "All" pill. Single-select; replaces the old accordion. */
export function HealthPills({
  categories,
  counts,
  total,
  selected,
  onSelect,
}: {
  categories: string[]
  counts: Map<string, number>
  total: number
  selected: string
  onSelect: (category: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3">
      <Pill
        active={selected === ALL_CATEGORY}
        onClick={() => onSelect(ALL_CATEGORY)}
        label="All"
        count={total}
      />
      {categories.map((category) => {
        const meta = GROUP_META[category]
        return (
          <Pill
            key={category}
            active={selected === category}
            onClick={() => onSelect(category)}
            icon={meta?.icon ?? 'circle'}
            varName={meta?.varName}
            label={category}
            count={counts.get(category) ?? 0}
          />
        )
      })}
    </div>
  )
}

export function HealthTable({ issues }: { issues: WorkIssue[] }) {
  const columns = useMemo<ColumnDef<WorkIssue>[]>(
    () => [
      {
        id: 'issue',
        accessorFn: (row) => row.number,
        header: 'Issue',
        meta: { headClassName: 'w-[40%]' },
        cell: ({ row }) => (
          <IssueLink
            number={row.original.number}
            className="flex min-w-0 items-center gap-2 hover:underline focus-visible:underline"
          >
            <span className="text-faint-foreground w-11 shrink-0 text-right font-mono text-[11px] tabular-nums">
              #{row.original.number}
            </span>
            <TypeGlyph typeName={row.original.typeName} />
            <span className="max-w-100 truncate font-medium">{row.original.title}</span>
          </IssueLink>
        ),
      },
      {
        id: 'priority',
        accessorFn: (row) => row.priority ?? '',
        header: 'Priority',
        cell: ({ row }) => <PriorityChip priority={row.original.priority} />,
      },
      {
        id: 'stage',
        accessorFn: (row) => row.stage ?? '',
        header: 'Stage',
        cell: ({ row }) => <StageBadge stage={row.original.stage} />,
      },
      {
        id: 'target',
        accessorFn: (row) => row.targetDate ?? '',
        header: 'Target',
        cell: ({ row }) => {
          const overdue = daysOverdue(row.original.targetDate, row.original.stage)
          if (overdue !== null) {
            return <span className="text-destructive font-medium">{overdue}d overdue</span>
          }
          return (
            <span className="text-muted-foreground font-mono text-[11px]">
              {row.original.targetDate ?? '—'}
            </span>
          )
        },
      },
      {
        id: 'inStage',
        accessorFn: (row) => daysAtStage(null, row.createdAt),
        header: 'Age',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground font-mono text-[11px]">{String(getValue())}d old</span>
        ),
      },
      {
        id: 'assignees',
        header: 'Assignees',
        enableSorting: false,
        cell: ({ row }) => <AvatarGroup users={row.original.assignees} />,
      },
    ],
    [],
  )

  return (
    <IssueDataTable
      issues={issues}
      columns={columns}
      sortable
      bulkKind="work"
      emptyTitle="Nothing needs attention."
    />
  )
}
